/** Target lightness lift at the gradient top stop (35–45% toward full lightness). */
export const BAR_GRADIENT_LIGHTNESS_LIFT = 0.4;

/** Hover/active bars get a touch more depth without shifting hue. */
export const BAR_GRADIENT_HOVER_LIGHTNESS_LIFT = 0.45;

/** Mid-stop position along the vertical ramp — smooths the falloff. */
export const BAR_GRADIENT_MID_OFFSET = "55%";

/**
 * Saturation eased down as lightness rises so the top stop stays in-hue
 * (e.g. green does not read yellow, coral does not read pink).
 */
const GRADIENT_SATURATION_EASE = 0.22;

export interface BarGradientStops {
  base: string;
  mid: string;
  top: string;
}

export function barGradientId(chartInstanceId: string, seriesKey: string): string {
  return `barGradient-${chartInstanceId}-${seriesKey}`;
}

export function barGradientHoverId(chartInstanceId: string, seriesKey: string): string {
  return `barGradientHover-${chartInstanceId}-${seriesKey}`;
}

/** Resolve `var(--token)` to the computed hex/rgb string on the document root. */
export function resolveCssColorVar(
  cssColor: string,
  root: Element | null = typeof document !== "undefined" ? document.documentElement : null,
): string {
  const varMatch = cssColor.match(/var\(\s*(--[^),\s]+)/);
  if (!varMatch || !root) return cssColor;
  const resolved = getComputedStyle(root).getPropertyValue(varMatch[1]).trim();
  return resolved || cssColor;
}

function parseHexColor(hex: string): { r: number; g: number; b: number } | null {
  const normalized = hex.replace("#", "").trim();
  if (!/^[\da-f]{6}$/i.test(normalized)) return null;
  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
  };
}

function toHex(channel: number): string {
  return Math.round(Math.min(255, Math.max(0, channel)))
    .toString(16)
    .padStart(2, "0");
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

interface Hsl {
  h: number;
  s: number;
  l: number;
}

function rgbToHsl(r: number, g: number, b: number): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  let h = 0;
  if (delta !== 0) {
    if (max === rn) h = ((gn - bn) / delta) % 6;
    else if (max === gn) h = (bn - rn) / delta + 2;
    else h = (rn - gn) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rn = 0;
  let gn = 0;
  let bn = 0;
  if (h < 60) [rn, gn, bn] = [c, x, 0];
  else if (h < 120) [rn, gn, bn] = [x, c, 0];
  else if (h < 180) [rn, gn, bn] = [0, c, x];
  else if (h < 240) [rn, gn, bn] = [0, x, c];
  else if (h < 300) [rn, gn, bn] = [x, 0, c];
  else [rn, gn, bn] = [c, 0, x];
  return {
    r: (rn + m) * 255,
    g: (gn + m) * 255,
    b: (bn + m) * 255,
  };
}

/**
 * Lighten in HSL — holds hue, raises lightness toward the target lift, and
 * eases saturation so the top stop does not drift into a neighboring hue.
 */
export function tintBarGradientStop(
  baseHex: string,
  lightnessLift = BAR_GRADIENT_LIGHTNESS_LIFT,
): string {
  const rgb = parseHexColor(baseHex);
  if (!rgb) return baseHex;
  const { h, s, l } = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const liftedL = l + (1 - l) * lightnessLift;
  const easedS = s * (1 - lightnessLift * GRADIENT_SATURATION_EASE);
  const next = hslToRgb(h, easedS, liftedL);
  return rgbToHex(next.r, next.g, next.b);
}

/** @deprecated Use {@link tintBarGradientStop} — kept for tests that assert RGB mix behavior. */
export function lightenHexColor(baseHex: string, amount = 0.15): string {
  const rgb = parseHexColor(baseHex);
  if (!rgb) return baseHex;
  const mix = (channel: number) => channel + (255 - channel) * amount;
  return rgbToHex(mix(rgb.r), mix(rgb.g), mix(rgb.b));
}

export function buildBarGradientStops(
  colorVar: string,
  root?: Element | null,
  lift = BAR_GRADIENT_LIGHTNESS_LIFT,
): BarGradientStops {
  const base = resolveCssColorVar(colorVar, root);
  return {
    base,
    mid: tintBarGradientStop(base, lift * 0.5),
    top: tintBarGradientStop(base, lift),
  };
}
